/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import SearchInput, {
  SearchInputOption,
  SearchInputProps,
  SearchInputRenderOption
} from '../../../../client/js/components/form/fields/SearchInput'
import { calculateAgencyFeesPercentage, getMandateReference } from '../../lib/common/mandate.mjs'
import { fetchMandate, findManyMandates } from '../../lib/rest/mandateAPIClient'
import LabelledValue from '../LabelledValue'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import MandateStatusBadge from '../mandate/MandateStatusBadge'
import { useLeaseAmountLabel } from '../mandate/useLeaseAmountLabel'
import { useAgencyFeesLabel } from '../mandate/useAgencyFeesLabel'
import { getMandateCreateLink, getMandateLink } from '../../lib/links/mandateLinks'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { ActionIcon, Box, Group, Text } from '@mantine/core'
import Amount from '../transaction/Amount'

function getMandateOptionLabel (mandate: Mandate) {
  return getMandateReference(mandate)
}

function MandateOption (props: SearchInputRenderOption<Mandate>) {
  const { option } = props
  const { item } = option
  const intl = useIntl()
  const percentage = calculateAgencyFeesPercentage(item)
  const agencyFeesLabel = useAgencyFeesLabel(item.type, percentage)
  const priceLabel = useLeaseAmountLabel(item.type)
  return (
    <Box w="100%">
      <Group align="start">
        <Text inherit>
          {getMandateOptionLabel(item)}
        </Text>
        <MandateStatusBadge
          ml="auto"
          mandate={item}
          size="sm"
        />
      </Group>
      <Box fz="sm" c="dimmed">
        {item.price != null
          ? <LabelledValue
            label={priceLabel}
            value={<Amount value={item.price} inherit withCurrency />}
          />
          : null}

        {item.securityDeposit != null
          ? <LabelledValue
            label={intl.formatMessage({
              id: 'securityDeposit',
              defaultMessage: 'Dépôt de garantie'
            })}
            value={<Amount value={item.securityDeposit} inherit withCurrency />}
          />
          : null}

        {item.agencyFees != null
          ? <LabelledValue
            label={agencyFeesLabel}
            value={<Amount value={item.agencyFees} inherit withCurrency />}
          />
          : null}

        {item.managementFees != null
          ? <LabelledValue
            label={intl.formatMessage({
              id: 'management_fees',
              defaultMessage: 'Honoraires de gestion'
            })}
            value={<Amount value={item.managementFees} inherit withCurrency />}
          />
          : null}

        <Group>
          <LabelledValue
            label={intl.formatMessage({
              id: 'beginning',
              defaultMessage: 'Début'
            })}
            value={intl.formatDate(item.startDate)}
          />
          {item.endDate
            ? <LabelledValue
              label={intl.formatMessage({
                id: 'ending',
                defaultMessage: 'Fin'
              })}
              value={intl.formatDate(item.endDate)}
            />
            : null}
        </Group>
      </Box>
    </Box>
  )
}

export type MandateInputProps = Omit<SearchInputProps<Mandate>, 'data'> & {
  queryFilter?: Record<string, any>,
}

function MandateInput (props: MandateInputProps) {
  const {
    disabled,
    id,
    label,
    name = 'mandateId',
    onChange,
    queryFilter,
    required,
    value,
    ...others
  } = props

  const fieldId = id || `${name}-field`

  const intl = useIntl()
  const [searchParams, setSearchParams] = useState({ limit: 5, search: null })

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['mandates', queryFilter, searchParams],
    queryFn: () => findManyMandates({
      ...queryFilter,
      ...searchParams
    }),
    refetchOnWindowFocus: false
  })

  const data = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.mandates : null) || []
  ), [listQuery.data])

  const options: SearchInputOption<Mandate>[] = useMemo(() => (
    data.map((el) => ({
      item: el,
      label: getMandateOptionLabel(el),
      value: String(el.id)
    }))
  ), [data])

  const setSearch = useCallback((search: string) => {
    setSearchParams((s) => ({ ...s, search }))
  }, [])

  const mandateQuery = useQuery({
    enabled: value != null && value !== '',
    queryKey: ['mandate', value],
    queryFn: () => fetchMandate(parseInt(value, 10)),
    refetchOnWindowFocus: false
  })
  const { data: mandate } = mandateQuery

  useEffect(() => {
    if (mandate != null && searchParams.search == null) {
      setSearchParams((s) => ({
        ...s, search: getMandateOptionLabel(mandate)
      }))
    }
  }, [mandate, searchParams.search])

  return (
    <SearchInput
      id={fieldId}
      label={label || intl.formatMessage({
        id: 'mandate',
        defaultMessage: 'Mandat'
      })}
      {...others}
      data={options}
      disabled={disabled}
      name={name}
      onChange={onChange}
      onSearchChange={setSearch}
      renderOption={MandateOption}
      required={required}
      searchValue={searchParams.search}
      value={value != null ? String(value) : null}
      rightSection={<>
        {value == null
          ? <ActionIcon
            component={AnchorLink}
            target="_blank"
            title={intl.formatMessage({
              id: 'new',
              defaultMessage: 'Nouveau'
            })}
            to={getMandateCreateLink()}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faPlusCircle}
            />
          </ActionIcon>
          : <ActionIcon
            disabled={!value}
            component={AnchorLink}
            target="_blank"
            to={getMandateLink(Number(value))}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faLink}
            />
          </ActionIcon>
        }
      </>}
    />
  )
}

export default MandateInput
